// YNX — TASKS MODULE (Kanban)
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, Trash2, X } from 'lucide-react';
import { listTasks, createTask, updateTask, deleteTask } from '../../lib/biz';

const COLUMNS = [
  { id: 'todo',  label: 'À faire',    color: '#fbbf24' },
  { id: 'doing', label: 'En cours',   color: '#a78bfa' },
  { id: 'done',  label: 'Terminé',    color: '#34d399' },
];

export const TasksModule = () => {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', priority: 'normal', due_date: '', status: 'todo' });
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try { setItems(await listTasks()); }
    catch (e) { console.warn('YNX tasks: refresh failed', e); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const add = async () => {
    if (!form.title.trim()) return;
    setBusy(true);
    try {
      await createTask(form);
      setForm({ title: '', description: '', priority: 'normal', due_date: '', status: 'todo' });
      setShowForm(false);
      await refresh();
    } finally { setBusy(false); }
  };

  const move = async (task, newStatus) => {
    await updateTask(task.id, { ...task, status: newStatus });
    await refresh();
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer cette tâche ?')) return;
    await deleteTask(id);
    await refresh();
  };

  // Memoize the bucketing of tasks per column so the JSX doesn't recompute it
  // on every render (cheap for small lists, but cleaner for hot reload).
  const tasksByCol = useMemo(() => {
    const buckets = { todo: [], doing: [], done: [] };
    for (const t of items) (buckets[t.status || 'todo'] ||= []).push(t);
    return buckets;
  }, [items]);

  return (
    <div className='biz-module'>
      <header className='biz-module-head'>
        <h2>📋 Tâches</h2>
        <button className='biz-btn-primary sm' onClick={() => setShowForm(true)} data-testid='task-add-btn'>
          <Plus size={14} /> Nouvelle tâche
        </button>
      </header>

      <div className='biz-kanban'>
        {COLUMNS.map((col) => {
          const colTasks = tasksByCol[col.id] || [];
          return (
            <div key={col.id} className='biz-kanban-col'>
              <h3 style={{ borderTopColor: col.color }}>
                {col.label} <span className='biz-kanban-count'>{colTasks.length}</span>
              </h3>
              <div className='biz-kanban-cards'>
                {colTasks.map((t) => (
                  <article key={t.id} className={`biz-task-card prio-${t.priority || 'normal'}`}>
                    <header>
                      <h4>{t.title}</h4>
                      <button className='biz-icon-btn danger' onClick={() => remove(t.id)}><Trash2 size={11} /></button>
                    </header>
                    {t.description && <p>{t.description}</p>}
                    <footer>
                      {t.due_date && <span className='biz-task-due'>⏱ {t.due_date}</span>}
                      <div className='biz-task-moves'>
                        {COLUMNS.filter((c) => c.id !== col.id).map((c) => (
                          <button key={c.id} className='biz-task-move-btn' onClick={() => move(t, c.id)} title={`→ ${c.label}`}>
                            → {c.label}
                          </button>
                        ))}
                      </div>
                    </footer>
                  </article>
                ))}
                {colTasks.length === 0 && <div className='biz-kanban-empty'>—</div>}
              </div>
            </div>
          );
        })}
      </div>

      {showForm && (
        <div className='biz-modal-overlay' onClick={(e) => e.target === e.currentTarget && setShowForm(false)}>
          <div className='biz-modal'>
            <header className='biz-modal-head'><h3>Nouvelle tâche</h3>
              <button className='biz-icon-btn' onClick={() => setShowForm(false)}><X size={14} /></button>
            </header>
            <div className='biz-modal-body'>
              <label className='biz-label'>Titre *</label>
              <input className='biz-input' name='tsk-title' value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder='Préparer la présentation client' />
              <label className='biz-label'>Description</label>
              <textarea className='biz-input' name='tsk-desc' rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              <label className='biz-label'>Priorité</label>
              <select className='biz-input' name='tsk-prio' value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                <option value='low'>Basse</option>
                <option value='normal'>Normale</option>
                <option value='high'>Haute</option>
                <option value='urgent'>Urgente</option>
              </select>
              <label className='biz-label'>Échéance</label>
              <input className='biz-input' name='tsk-due' type='date' value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
              <label className='biz-label'>Statut initial</label>
              <select className='biz-input' name='tsk-status' value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                {COLUMNS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <footer className='biz-modal-foot'>
              <button className='biz-btn-ghost' onClick={() => setShowForm(false)}>Annuler</button>
              <button className='biz-btn-primary' onClick={add} disabled={busy} data-testid='tsk-save-btn'>
                {busy ? 'Enregistrement…' : 'Créer la tâche'}
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
};
